import { YandexGeoObject } from "./yandexMaps";

export type ClassifiedAddress = {
  address_region: string;
  address_locality: string;
  address_street: string;
  address_house: string;
};

const getYandexComponents = (geoObject: YandexGeoObject): unknown[] => {
  const rawComponents = geoObject.properties?.get(
    "metaDataProperty.GeocoderMetaData.Address.Components",
    [],
  );
  return Array.isArray(rawComponents) ? rawComponents : [];
};

const getYandexComponentName = (
  components: unknown[],
  kinds: string[],
): string => {
  for (const kind of kinds) {
    const component = components.find((item) => {
      if (!item || typeof item !== "object") return false;
      return String((item as { kind?: unknown }).kind || "") === kind;
    });
    const name = String(
      (component as { name?: unknown } | undefined)?.name || "",
    ).trim();
    if (name) return name;
  }
  return "";
};

const firstNonEmpty = (...values: unknown[]): string => {
  for (const value of values) {
    const normalized = String(value || "").trim();
    if (normalized) return normalized;
  }
  return "";
};

const isFederalDistrict = (value: string): boolean =>
  /\bфедеральн(?:ый|ого)\s+округ\b/i.test(value);

const isRussianFederalSubject = (value: string): boolean =>
  /(?:^|\s)республика(?:\s|$)|\bкрай\b|\bобласть\b|\bавтономн(?:ая|ый)\s+округ\b|\bавтономная\s+область\b|^(?:Москва|Санкт-Петербург|Севастополь)$/i.test(
    value,
  );

const getAddressRegion = (
  components: unknown[],
  administrativeAreas: string[],
): string => {
  const componentNames = components.flatMap((item) => {
    if (!item || typeof item !== "object") return [];
    const name = String((item as { name?: unknown }).name || "").trim();
    return name ? [name] : [];
  });

  // Yandex can mark a federal district as `province` and the actual subject as
  // `area`. Prefer names that are recognisable Russian federal subjects instead.
  const russianSubject = firstNonEmpty(
    ...componentNames.filter(
      (name) => isRussianFederalSubject(name) && !isFederalDistrict(name),
    ),
    ...administrativeAreas.filter(
      (name) => isRussianFederalSubject(name) && !isFederalDistrict(name),
    ),
  );
  if (russianSubject) return russianSubject;

  const providerRegion = getYandexComponentName(components, [
    "province",
    "area",
  ]);
  if (providerRegion && !isFederalDistrict(providerRegion))
    return providerRegion;

  return firstNonEmpty(
    ...administrativeAreas.filter((name) => !isFederalDistrict(name)),
    getYandexComponentName(components, ["country"]),
  );
};

export const extractAddressFromGeoObject = (
  geoObject: YandexGeoObject,
): ClassifiedAddress => {
  const components = getYandexComponents(geoObject);
  const administrativeAreas = geoObject.getAdministrativeAreas?.() || [];
  const localities = geoObject.getLocalities?.() || [];

  return {
    address_region: firstNonEmpty(
      getAddressRegion(components, administrativeAreas),
      geoObject.getCountry?.(),
    ),
    address_locality: firstNonEmpty(
      getYandexComponentName(components, ["locality", "district"]),
      localities[0],
    ),
    address_street: firstNonEmpty(
      getYandexComponentName(components, ["street", "route"]),
      geoObject.getThoroughfare?.(),
    ),
    address_house: firstNonEmpty(
      getYandexComponentName(components, ["house"]),
      geoObject.getPremiseNumber?.(),
    ),
  };
};
